import {
  Autocomplete,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Icons,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Typography,
} from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  getDirectlyObservedTherapyByTherapyQueryKey,
  useCreateDirectlyObservedTherapyByPatient,
} from '../../../../../api/created/directlyObservedTherapy.ts';
import {
  AddressContactDto,
  AddressDto,
  AddressDtoAddressType,
  PatientAddressDtoReferenceType,
} from '../../../../../api/generated/model';
import {
  getGetTherapyByPatientQueryKey,
  useGetPatientById,
} from '../../../../../api/generated/patient-controller/patient-controller.ts';
import { FormActionButtons } from '../../../../../components/form/FormActionButtons.tsx';
import { GermanDateInput } from '../../../../../components/form/GermanDateInput.tsx';
import { InstitutionContactFields } from '../../../../../components/form/InstitutionContactFields.tsx';
import { formatDateForInput, getCurrentDate, getDateAfterInputValue } from '../../../../../utils/date.ts';
import { DOT_FREQUENCY_OPTIONS, toDotFrequencyPayload } from '../../../../../utils/dotFrequency.ts';
import { getStringOrUndefined } from '../../../../../utils/string.ts';

interface FormValues {
  executorAddress: AddressDto | null;
  executorContact: AddressContactDto | null;
  applicantAddress: AddressDto | null;
  applicantContact: AddressContactDto | null;
  from: string;
  until: string;
  frequenz: string;
}

const DOT_ADDRESS_TYPES = Object.values(AddressDtoAddressType);

export function CreateDirectlyObservedTherapyDialog({
  open,
  onClose,
  patientId,
  therapyId,
}: {
  open: boolean;
  onClose: () => void;
  patientId: string;
  therapyId: string;
}) {
  const queryClient = useQueryClient();
  const { data: patient, isPending } = useGetPatientById(patientId);
  const {
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty: isFormDirty },
  } = useForm<FormValues>({
    defaultValues: {
      executorAddress: null,
      executorContact: null,
      applicantAddress: null,
      applicantContact: null,
      from: formatDateForInput(getCurrentDate()),
      until: '',
      frequenz: '',
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const fromValue = watch('from');
  const untilMinDate = getDateAfterInputValue(fromValue);

  const createDirectlyObservedTherapyMutation = useCreateDirectlyObservedTherapyByPatient({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: getGetTherapyByPatientQueryKey(patientId) });
      void queryClient.invalidateQueries({ queryKey: getDirectlyObservedTherapyByTherapyQueryKey(therapyId) });
      handleOnClose();
    },
    onError: () => toast.error('Beim erstellen der DOT ist ein Fehler aufgetreten'),
  });

  const handleOnClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleOnClose}>
      <ModalDialog sx={{ width: 750 }}>
        <ModalClose />
        <Typography level={'h4'}>DOT erfassen</Typography>
        <Divider />
        <form
          onSubmit={handleSubmit((data) =>
            createDirectlyObservedTherapyMutation.mutate({
              patientId,
              data: {
                from: data.from,
                until: getStringOrUndefined(data.until),
                executor: {
                  address: data.executorAddress!,
                  contact: data.executorContact ?? undefined,
                  from: data.from,
                  until: getStringOrUndefined(data.until),
                  patientId: patientId,
                  referenceType: PatientAddressDtoReferenceType.THERAPY_DOT_EXECUTOR,
                },
                applicant: {
                  address: data.applicantAddress!,
                  contact: data.applicantContact ?? undefined,
                  from: data.from,
                  until: getStringOrUndefined(data.until),
                  patientId: patientId,
                  referenceType: PatientAddressDtoReferenceType.THERAPY_DOT_APPLICANT,
                },
                frequenz: toDotFrequencyPayload(data.frequenz),
              },
            }),
          )}>
          <Stack gap={2}>
            <Stack direction={'row'} gap={2}>
              <FormControl sx={{ flex: 1 }} error={!!errors.from}>
                <FormLabel>
                  <Typography>Startdatum *</Typography>
                </FormLabel>
                <GermanDateInput
                  control={control}
                  name="from"
                  rules={{
                    required: true,
                  }}
                />
                {errors.from && <FormHelperText>Datum muss ausgefüllt werden</FormHelperText>}
              </FormControl>
              <FormControl sx={{ flex: 1 }} error={!!errors.until}>
                <FormLabel>
                  <Typography>Enddatum</Typography>
                </FormLabel>
                <GermanDateInput
                  control={control}
                  name="until"
                  rules={{
                    required: false,
                    ...(untilMinDate ? { min: { value: untilMinDate, message: 'range' } } : {}),
                  }}
                  min={untilMinDate}
                />
                {errors.until && <FormHelperText>Enddatum muss nach dem Startdatum liegen</FormHelperText>}
              </FormControl>
            </Stack>
            <FormControl>
              <FormLabel>
                <Typography>DOT Frequenz</Typography>
              </FormLabel>
              <Controller
                name="frequenz"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Autocomplete
                    freeSolo
                    value={value ?? ''}
                    inputValue={value ?? ''}
                    onChange={(_, newValue) => onChange(newValue ?? '')}
                    onInputChange={(_, newInputValue) => onChange(newInputValue)}
                    options={DOT_FREQUENCY_OPTIONS.map((option) => option.label)}
                    variant="outlined"
                  />
                )}
              />
            </FormControl>
            <InstitutionContactFields
              formMethods={{ control, watch, setValue }}
              addressField="applicantAddress"
              contactField="applicantContact"
              addressLabel="DOT Auftraggeber *"
              contactLabel="Auftraggeber Kontakt"
              addressTypes={DOT_ADDRESS_TYPES}
              canChangeAddressType={true}
              addressRules={{ required: true }}
              addressError={!!errors.applicantAddress}
              addressHelperText={errors.applicantAddress ? 'DOT Auftragsgeber muss angegeben werden' : undefined}
              clearContactOnAddressChange="always"
              contactClearOptions={false}
              useAllContactsList={true}
            />
            <InstitutionContactFields
              formMethods={{ control, watch, setValue }}
              addressField="executorAddress"
              contactField="executorContact"
              addressLabel="DOT Durchführer *"
              contactLabel="Durchführer Kontakt"
              addressTypes={DOT_ADDRESS_TYPES}
              canChangeAddressType={false}
              addressRules={{ required: true }}
              addressError={!!errors.executorAddress}
              addressHelperText={errors.executorAddress ? 'DOT Durchführer muss angegeben werden' : undefined}
              clearContactOnAddressChange="always"
              contactClearOptions={false}
              useAllContactsList={true}
            />
            <Button
              component={'a'}
              loading={isPending}
              disabled={
                !(patient?.address?.address.address ?? patient?.address?.address.city ?? patient?.address?.address.zip)
              }
              target={'_blank'}
              href={`https://www.google.ch/maps/search/apotheke+near+${patient?.address?.address.address ?? ''}+${patient?.address?.address.city ?? ''}+${patient?.address?.address.zip ?? ''}`}
              variant={'outlined'}
              endDecorator={<Icons.Map />}>
              Apotheken in Wohnortnähe anzeigen
            </Button>
            <FormActionButtons
              onAbortClick={handleOnClose}
              disableSubmit={!isFormDirty}
              isLoading={createDirectlyObservedTherapyMutation.isPending}
            />
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
}
